/**
 * PURPOSE: Estado y acciones de las firmas digitales dentro de la Vista Planilla FEDUP (FTR-0062).
 * RESPONSIBILITIES:
 *  - Sí: cargar el estado de las casillas al abrir el visor y al activar un modo (otra pestaña pudo firmar); un modo activo a la vez
 *    (pulsar el mismo lo desactiva); abrir el pad de una casilla o, con el partido oficializado, el aviso del candado.
 *  - Sí: firmar/rehacer/quitar con `expectedSignatureId`; tras escribir, pedir el PDF de nuevo (conservando zoom y scroll, eso lo hace
 *    el visor) y mostrar al instante la firma nueva (o tapar la quitada) hasta que llegue el PDF regenerado.
 *  - Sí: tolerar que el partido se oficialice en otra pestaña (423 → se refresca el estado y el pad pasa a candado), conflictos (409 →
 *    se refresca todo) y un backend sin la tabla (503 → el selector se oculta: el visor queda como antes).
 *  - Sí: imágenes de las firmas (Blob → URL local, liberadas al cerrar).
 *  - No: no toca la planilla digital ni guarda nada en localStorage/sessionStorage.
 * COLLABORATORS:
 *  - FedupPreviewModal.jsx (lo usa), fedupSignatures.utils.js, el servicio de firmas del deporte (`signatures`)
 * ROUTE: src/components/FullScreenModal/FullScreenPlanilla/shared/fedupSignatures/useFedupSignatureController.js
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { classifySignatureError, getSignatureDeviceId, isSlotLocked } from "./fedupSignatures.utils.js";

export function useFedupSignatureController({ service, matchId, onPdfChanged }) {
  const enabled = Boolean(service && matchId);
  const [status, setStatus] = useState(null);
  const [loadState, setLoadState] = useState(enabled ? "loading" : "idle");
  const [mode, setMode] = useState(null);
  /** Cada vez que se elige un modo: { n, mode, fromNone } (el visor se desplaza a la zona y, si venía de ninguno, recarga el iframe). */
  const [activation, setActivation] = useState({ n: 0, mode: null, fromNone: false });
  const modeRef = useRef(null);
  modeRef.current = mode;
  const [padSlotId, setPadSlotId] = useState(null);
  const [padError, setPadError] = useState(null);
  const [padLocked, setPadLocked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [lockSlotId, setLockSlotId] = useState(null);
  const [previews, setPreviews] = useState({});
  const [notice, setNotice] = useState(null);
  const [images, setImages] = useState({});

  const controllerRef = useRef(null);
  const requestRef = useRef(0);
  const mountedRef = useRef(true);
  const imageUrlsRef = useRef(new Map());
  const imageLoadsRef = useRef(new Map());
  const serviceRef = useRef(service);
  serviceRef.current = service;
  const onPdfChangedRef = useRef(onPdfChanged);
  onPdfChangedRef.current = onPdfChanged;

  const refresh = useCallback(async () => {
    if (!enabled) return null;
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    const id = ++requestRef.current;
    setLoadState((prev) => (prev === "ready" ? "ready" : "loading"));
    try {
      const next = await serviceRef.current.loadStatus(matchId, { signal: controller.signal });
      if (!mountedRef.current || id !== requestRef.current) return null;
      setStatus(next);
      setLoadState("ready");
      return next;
    } catch (err) {
      if (!mountedRef.current || id !== requestRef.current) return null;
      const info = classifySignatureError(err);
      if (info.kind === "canceled") return null;
      // 503 SIGNATURES_NOT_AVAILABLE (backend sin la tabla) o 404 (backend sin estas rutas: web desplegada antes que el backend):
      // las firmas no existen en este servidor → el visor queda como antes, sin selector.
      setLoadState(info.kind === "unavailable" || info.status === 404 ? "unavailable" : "error");
      return null;
    }
  }, [enabled, matchId]);

  useEffect(() => {
    mountedRef.current = true;
    if (enabled) refresh();
    const urls = imageUrlsRef.current;
    return () => {
      mountedRef.current = false;
      controllerRef.current?.abort();
      for (const url of urls.values()) window.URL.revokeObjectURL(url);
      urls.clear();
    };
  }, [enabled, refresh]);

  const official = Boolean(status?.official);
  const slotById = useMemo(() => new Map((status?.slots || []).map((s) => [s.slot, s])), [status]);
  const padSlot = padSlotId ? slotById.get(padSlotId) || null : null;

  const toggleMode = useCallback(
    (next) => {
      const current = modeRef.current;
      const value = current === next ? null : next;
      setLockSlotId(null);
      setNotice(null);
      setMode(value);
      if (value) {
        setActivation((prev) => ({ n: prev.n + 1, mode: value, fromNone: !current }));
        refresh();
      }
    },
    [refresh]
  );

  const openSlot = useCallback(
    (slotId) => {
      if (!slotById.has(slotId)) return;
      setNotice(null);
      // Oficializado: solo una casilla firmada muestra el candado; una vacía abre el pad (con la nota de que no se podrá cambiar).
      if (isSlotLocked(slotById.get(slotId), official)) {
        setLockSlotId(slotId);
        return;
      }
      setLockSlotId(null);
      setPadError(null);
      setPadLocked(false);
      setPadSlotId(slotId);
    },
    [official, slotById]
  );

  const closePad = useCallback(() => {
    if (busy) return;
    setPadSlotId(null);
    setPadError(null);
    setPadLocked(false);
  }, [busy]);

  const dismissLock = useCallback(() => setLockSlotId(null), []);

  /** Tras un 409/423 o una escritura: estado y PDF frescos. */
  const resync = useCallback(async ({ reloadPdf = true } = {}) => {
    await refresh();
    if (reloadPdf) onPdfChangedRef.current?.();
  }, [refresh]);

  const handleWriteError = useCallback(
    async (err) => {
      const info = classifySignatureError(err);
      if (info.kind === "canceled") return;
      if (info.kind === "official") {
        setPadLocked(true);
        setPadError(null);
        await refresh();
        return;
      }
      if (info.kind === "unavailable") {
        setPadSlotId(null);
        setLoadState("unavailable");
        return;
      }
      setPadError(info);
      if (info.kind === "conflict" || info.kind === "notFound") await resync();
    },
    [refresh, resync]
  );

  const save = useCallback(
    async (slotId, png) => {
      const slot = slotById.get(slotId);
      if (!slot || !png?.dataUrl || busy) return false;
      setBusy(true);
      setPadError(null);
      try {
        const out = await serviceRef.current.save(matchId, slotId, {
          dataUrl: png.dataUrl,
          expectedSignatureId: slot.signature?.id ?? null,
          deviceId: getSignatureDeviceId(),
        });
        if (!mountedRef.current) return true;
        if (out?.status) setStatus(out.status);
        setPreviews((prev) => ({ ...prev, [slotId]: { dataUrl: png.dataUrl, width: png.width, height: png.height } }));
        setPadSlotId(null);
        onPdfChangedRef.current?.();
        return true;
      } catch (err) {
        if (mountedRef.current) await handleWriteError(err);
        return false;
      } finally {
        if (mountedRef.current) setBusy(false);
      }
    },
    [busy, handleWriteError, matchId, slotById]
  );

  const remove = useCallback(
    async (slotId) => {
      const slot = slotById.get(slotId);
      if (!slot?.signature || busy) return false;
      setBusy(true);
      setPadError(null);
      try {
        const out = await serviceRef.current.remove(matchId, slotId, { expectedSignatureId: slot.signature.id });
        if (!mountedRef.current) return true;
        if (out?.status) setStatus(out.status);
        setPreviews((prev) => ({ ...prev, [slotId]: { removed: true } }));
        setPadSlotId(null);
        onPdfChangedRef.current?.();
        return true;
      } catch (err) {
        if (mountedRef.current) await handleWriteError(err);
        return false;
      } finally {
        if (mountedRef.current) setBusy(false);
      }
    },
    [busy, handleWriteError, matchId, slotById]
  );

  /** Las vistas previas instantáneas se retiran cuando el PDF regenerado ya está a la vista. */
  const clearPreviews = useCallback(() => setPreviews((prev) => (Object.keys(prev).length ? {} : prev)), []);

  /** URL local de la imagen de la firma activa de una casilla (se pide una vez por firma). */
  const ensureImage = useCallback(
    async (slotId) => {
      const slot = slotById.get(slotId);
      const signatureId = slot?.signature?.id;
      if (!signatureId || !serviceRef.current?.loadImage) return null;
      const key = `${slotId}:${signatureId}`;
      if (imageUrlsRef.current.has(key)) return imageUrlsRef.current.get(key);
      if (imageLoadsRef.current.has(key)) return imageLoadsRef.current.get(key);
      const load = (async () => {
        try {
          const blob = await serviceRef.current.loadImage(matchId, slotId);
          if (!mountedRef.current || !blob) return null;
          const url = window.URL.createObjectURL(blob);
          imageUrlsRef.current.set(key, url);
          setImages((prev) => ({ ...prev, [key]: url }));
          return url;
        } catch {
          return null;
        } finally {
          imageLoadsRef.current.delete(key);
        }
      })();
      imageLoadsRef.current.set(key, load);
      return load;
    },
    [matchId, slotById]
  );

  const imageFor = useCallback(
    (slotId) => {
      const slot = slotById.get(slotId);
      return slot?.signature ? images[`${slotId}:${slot.signature.id}`] || null : null;
    },
    [images, slotById]
  );

  return {
    enabled,
    status,
    loadState,
    available: enabled && loadState !== "unavailable",
    official,
    mode,
    activation,
    toggleMode,
    slotById,
    padSlot,
    padError,
    padLocked,
    busy,
    openSlot,
    closePad,
    save,
    remove,
    lockSlotId,
    dismissLock,
    previews,
    clearPreviews,
    notice,
    setNotice,
    refresh,
    ensureImage,
    imageFor,
  };
}
